/**
 * The auth context and the hook that reads it. The provider that fills it — and
 * the Firebase SDK wiring behind it — lives in AuthProvider.tsx, so this module
 * exports no component: react-refresh only preserves state across an edit when a
 * module's exports are all components or all not, and every screen reads auth.
 *
 * Kept at this path on purpose. Five components and every test mock import
 * `useAuth` from "./auth.js"; moving the hook instead of the provider would have
 * rewritten all of them, and a test whose `vi.mock("./auth.js")` silently stopped
 * covering `useAuth` would reach for the real Firebase SDK rather than fail
 * loudly.
 */
import { createContext, useContext } from "react";
import { type User } from "firebase/auth";

export type AuthState = {
  /** undefined while Firebase is still working out whether a session exists. */
  user: User | null | undefined;
  signIn(email: string, password: string): Promise<void>;
  /**
   * Creates the Firebase identity ONLY. It does not create a TitleVerify
   * account, and the naming is deliberate: the two are separate systems and the
   * second one can fail on its own. Whoever calls this must then provision the
   * account (POST /accounts) and deal honestly with the gap if that step fails —
   * see `provisionAccount` and `FinishSetup` in App.tsx.
   */
  createIdentity(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Asks Firebase to email a password-reset link.
   *
   * RESOLVES THE SAME WAY WHETHER OR NOT THE ADDRESS HAS AN ACCOUNT, and the
   * caller must show the same message either way. Anything else turns this form
   * into an oracle for "does this person have a land purchase in progress" —
   * which, for a service whose users are mid-transaction on property, is a
   * disclosure worth more than most. The implementation swallows the
   * `auth/user-not-found` class of error for exactly that reason; a genuine
   * transport failure still rejects, because a user who is offline needs to know
   * the request never left.
   *
   * NOTE this is the ONE mail this product causes to be sent, and it is sent by
   * Firebase Auth, not by us — there is still no mailer in apps/api/src. It is
   * also user-initiated, so it does not make NO_NOTIFICATION_NOTE false: nothing
   * reaches a client about their CASE.
   */
  sendPasswordReset(email: string): Promise<void>;
  /** A fresh ID token for an API call, or null when signed out. */
  getToken(): Promise<string | null>;
};

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
